import { Lock } from "lucide-react";

const Section = ({ emoji, title, subtitle, children }) => (
  <div className="mt-8 first:mt-0">
    <h4 className="font-heading font-bold text-lg sm:text-xl text-white flex items-center gap-2.5">
      <span className="text-xl leading-none">{emoji}</span>
      <span className="text-gradient-accent">{title}</span>
    </h4>
    {subtitle && (
      <p className="mt-2 font-mono text-xs uppercase tracking-[0.18em] text-cyan-400/80">
        {subtitle}
      </p>
    )}
    <div className="mt-3 space-y-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed">
      {children}
    </div>
  </div>
);

const purposes = [
  "Team allocations",
  "Treasury holdings",
  "Community allocations",
  "Marketing allocations",
  "Project reserves",
  "Long-term commitments",
];

export const LockerDetail = () => {
  return (
    <div data-testid="locker-detail-content">
      <Section emoji="🔒" title="Token & LP Lockers" subtitle="Lock It. Prove It. Protect It.">
        <p>
          Lockers are one of the many tools offered by Autono Dex to help projects demonstrate
          greater transparency and give their communities additional confidence.
        </p>
        <p>
          Whether you're a token creator, liquidity provider, or project team, Autono Dex allows you
          to permanently lock tokens or liquidity-provider (LP) assets directly on-chain.
        </p>
      </Section>

      <Section emoji="🔐" title="Permanent Locking">
        <p>
          Choose the asset you want to lock, specify the amount, and permanently send it into the
          locker contract.
        </p>
        <p>Once a permanent lock is created, it cannot be unlocked.</p>
        <div className="grid grid-cols-2 gap-2 pt-1">
          {[
            "Not by the project creator",
            "Not by the token deployer",
            "Not by the locker creator",
            "Not by Autono Dex",
          ].map((x) => (
            <div
              key={x}
              className="rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-xs text-slate-200"
            >
              {x}
            </div>
          ))}
        </div>
        <p className="text-slate-400">
          The goal is to make the lock enforceable by the smart contract rather than dependent on
          trust in a person or centralized administrator.
        </p>
      </Section>

      <Section emoji="💧" title="Lock Your Liquidity">
        <p>
          Liquidity locks can help projects demonstrate that liquidity has been committed and cannot
          simply be withdrawn by the project team.
        </p>
        <p>
          Lock your eligible LP tokens and provide your community with an on-chain record showing
          that the liquidity has been permanently committed.
        </p>
      </Section>

      <Section emoji="🪙" title="Lock Your Tokens">
        <p>Projects can also permanently lock tokens for purposes such as:</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {purposes.map((p) => (
            <span
              key={p}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200"
            >
              {p}
            </span>
          ))}
        </div>
        <p className="text-slate-400">
          Once permanently locked, those assets remain locked according to the rules of the deployed
          locker contract.
        </p>
      </Section>

      <Section emoji="🔎" title="Fully Transparent On-Chain">
        <p>
          Every locker is designed to provide an on-chain record that users can independently
          inspect. Your community can verify:
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <span className="inline-flex items-center rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-1.5 font-mono text-xs sm:text-sm text-cyan-300">
            What → How much → Which contract → When
          </span>
        </div>
        <p className="font-semibold text-white pt-1">
          No promises. No screenshots. Verify it on-chain.
        </p>
      </Section>

      <div className="mt-8 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
        <p className="font-heading font-bold text-amber-300 text-sm flex items-center gap-2">
          <span className="text-base">⚠️</span> Important
        </p>
        <p className="mt-2 text-sm text-slate-300">
          Permanent means permanent. Before creating a permanent lock, carefully verify the token,
          LP asset, amount, and destination. Assets sent into an irreversible locker may not be
          recoverable.
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-purple-600/5 p-5 flex items-start gap-3">
        <Lock className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-heading font-bold text-white">Autono Dex Lockers</p>
          <p className="mt-1 text-sm text-slate-300">
            Lock your assets. Strengthen your transparency. Prove your commitment. 🔒
          </p>
        </div>
      </div>
    </div>
  );
};
